/* eslint-disable class-methods-use-this */
import { Component } from '../component';
import { Element } from '../element';
import { safeCallback } from '../func_arr';
import { MutationObserver } from '../mutation_observer';
import { isComponent, isNativeNode } from '../type_symbol';
import { dispatchError, triggerWarning } from '../warning';
import { RangeListManager } from './range_list_diff';
const defaultChangePropFilter = (x) => x;
const defaultEventListenerWrapper = (elem, evName, listener) => (e) => { var _a; return listener.call((_a = elem.ownerShadowRoot) === null || _a === void 0 ? void 0 : _a.getHostNode().getMethodCaller(), e); };
export const getTmplArgs = (elem) => {
    const node = elem;
    // eslint-disable-next-line no-return-assign
    return (node._$wxTmplArgs = node._$wxTmplArgs || {});
};
export const getTmplDevArgs = (elem) => {
    const node = elem;
    // eslint-disable-next-line no-return-assign
    return (node._$wxTmplDevArgs = node._$wxTmplDevArgs || {});
};
export const dataValueToString = (v) => {
    if (v === null || v === undefined) {
        return '';
    }
    return String(v);
};
const dashToCamelCase = (dash) => {
    const ret = dash.replace(/-(.|$)/g, (s) => (s[1] ? s[1].toUpperCase() : ''));
    return ret;
};
const camelToDashCase = (dash) => {
    const ret = dash.replace(/[A-Z]/g, (s) => `-${s.toLowerCase()}`);
    return ret;
};
export class ProcGenWrapper {
    constructor(shadowRoot, procGen, fallbackListenerOnNativeNode) {
        this.bindingMapDisabled = false;
        this.changePropFilter = defaultChangePropFilter;
        this.eventListenerWrapper = defaultEventListenerWrapper;
        // set slot
        // (not used any more, leaving for compatibilities)
        this.s = (elem, v) => {
            elem.slot = v;
            this.tryCallPropertyChangeListener(elem, 'slot', v);
        };
        // set slot value
        this.l = (elem, name, value, generalLvaluePath) => {
            var _a;
            if (this.shadowRoot.getSlotMode() === 3 /* SlotMode.Dynamic */) {
                this.shadowRoot.replaceSlotValue(elem, name, value);
            }
            else {
                // compatibilities for legacy event binding syntax
                if (!this.checkFallbackEventListener(elem, name, value, generalLvaluePath)) {
                    triggerWarning(`"${name}" is not a valid event binding or slot value`, (_a = elem.ownerShadowRoot) === null || _a === void 0 ? void 0 : _a.getHostNode(), elem);
                }
            }
            this.tryCallPropertyChangeListener(elem, name, value);
        };
        // set id
        this.i = (elem, v) => {
            elem.id = v;
            this.tryCallPropertyChangeListener(elem, 'id', v);
        };
        // set class or external classes named `class`
        this.c = (elem, v) => {
            if (isComponent(elem)) {
                // "class" itself can also be an external class
                const hasExternalClass = elem.hasExternalClass('class');
                if (hasExternalClass) {
                    elem.setExternalClass('class', v);
                }
            }
            elem.setNodeClass(v);
            this.tryCallPropertyChangeListener(elem, 'class', v);
        };
        // set a list of classes and use previous value if an item is null
        this.e = (elem, classNames) => {
            const tmplArgs = getTmplArgs(elem);
            if (!tmplArgs.staticClasses)
                tmplArgs.staticClasses = new Array(classNames.length);
            const arr = tmplArgs.staticClasses;
            for (let i = 0; i < classNames.length; i += 1) {
                const item = classNames[i];
                if (typeof item === 'string') {
                    arr[i] = item;
                }
            }
            this.applyClassListUpdates(elem);
        };
        // set a single class in the list of classes (should only be used in binding-map updates)
        this.ei = (elem, index, className) => {
            const tmplArgs = getTmplArgs(elem);
            if (!tmplArgs.staticClasses)
                tmplArgs.staticClasses = [];
            const arr = tmplArgs.staticClasses;
            arr[index] = className;
            tmplArgs.staticClassesDirty = true;
        };
        // set style (or property named `style`)
        this.y = (elem, v) => {
            if (isComponent(elem) && Component.getDataProxy(elem).replaceProperty('style', v)) {
                // empty
            }
            else {
                elem.setNodeStyle(dataValueToString(v), 0 /* StyleSegmentIndex.MAIN */);
            }
            this.tryCallPropertyChangeListener(elem, 'style', v);
        };
        // set a list of styles and use previous value if an item is null
        this.w = (elem, styles) => {
            const tmplArgs = getTmplArgs(elem);
            if (!tmplArgs.styleNameValues)
                tmplArgs.styleNameValues = new Array(styles.length);
            const arr = tmplArgs.styleNameValues;
            for (let i = 0; i < styles.length; i += 2) {
                const value = styles[i + 1];
                if (typeof value === 'string') {
                    arr[i] = styles[i];
                    arr[i + 1] = value;
                }
            }
            this.applyStyleListUpdates(elem);
        };
        // set a single style value in the list of styles (should only be used in binding-map updates)
        this.wi = (elem, valueIndex, newValue) => {
            const tmplArgs = getTmplArgs(elem);
            if (!tmplArgs.styleNameValues)
                tmplArgs.styleNameValues = [];
            const arr = tmplArgs.styleNameValues;
            const nvIndex = valueIndex * 2;
            arr[nvIndex + 1] = newValue;
            tmplArgs.styleNameValuesDirty = true;
        };
        // set dataset
        this.d = (elem, name, v, fallbackDataHyphen) => {
            if (fallbackDataHyphen && isComponent(elem)) {
                const nodeDataProxy = Component.getDataProxy(elem);
                const fullName = `data${name[0].toUpperCase()}${name.slice(1)}`;
                if (nodeDataProxy.replaceProperty(fullName, v)) {
                    // empty
                }
                else {
                    const fullDashName = `data-${camelToDashCase(name)}`;
                    if (elem.hasExternalClass(fullDashName)) {
                        elem.setExternalClass(fullDashName, v);
                    }
                    else if (nodeDataProxy.replaceProperty(fullDashName, v)) {
                        // leave for compatibilities
                    }
                    else {
                        elem.setDataset(name, v);
                        elem.setAttribute(fullDashName, v);
                    }
                }
                this.tryCallPropertyChangeListener(elem, name, v);
            }
            else {
                elem.setDataset(name, v);
                if (fallbackDataHyphen) {
                    const fullDashName = `data-${camelToDashCase(name)}`;
                    elem.setAttribute(fullDashName, v);
                }
            }
        };
        // set mark
        this.m = (elem, name, v) => {
            elem.setMark(name, v);
        };
        // set event handler
        this.v = (elem, evName, v, final, mutated, capture, isDynamic, generalLvaluePath) => {
            const handler = typeof v === 'function' ? v : dataValueToString(v);
            const listener = this.eventListenerWrapper(elem, evName, function (...args) {
                const host = elem.ownerShadowRoot.getHostNode();
                const f = typeof handler === 'function' ? handler : Component.getMethod(host, handler);
                if (typeof f === 'function') {
                    return f.call(this, ...args);
                }
                return undefined;
            }, final, mutated, capture, generalLvaluePath);
            if (listener) {
                Object.defineProperty(listener, 'name', {
                    value: typeof handler === 'string' ? handler : handler.name,
                });
            }
            const evOptions = {
                final,
                mutated,
                capture,
            };
            if (isDynamic) {
                const tmplArgs = getTmplArgs(elem);
                if (!tmplArgs.dynEvListeners)
                    tmplArgs.dynEvListeners = {};
                const dynEvListeners = tmplArgs.dynEvListeners;
                if (dynEvListeners[evName]) {
                    elem.removeListener(evName, dynEvListeners[evName], evOptions);
                }
                dynEvListeners[evName] = listener;
            }
            if (handler && listener)
                elem.addListener(evName, listener, evOptions);
        };
        // update a property or external class of a component, or an attribute of a native node
        this.r = (elem, name, v, modelLvaluePath, generalLvaluePath) => {
            var _a;
            if (isComponent(elem)) {
                const nodeDataProxy = Component.getDataProxy(elem);
                const camelName = dashToCamelCase(name);
                if (nodeDataProxy.replaceProperty(camelName, v)) {
                    if (modelLvaluePath !== undefined) {
                        if (modelLvaluePath === null) {
                            nodeDataProxy.setModelBindingListener(camelName, () => { });
                        }
                        else {
                            nodeDataProxy.setModelBindingListener(camelName, (value) => {
                                const host = elem.ownerShadowRoot.getHostNode();
                                const nodeDataProxy = Component.getDataProxy(host);
                                nodeDataProxy.replaceDataOnPath(modelLvaluePath, value);
                                nodeDataProxy.applyDataUpdates(false);
                            });
                        }
                    }
                }
                else if (elem.hasExternalClass(name)) {
                    elem.setExternalClass(name, v);
                }
                else {
                    // compatibilities for legacy event binding syntax
                    if (!this.checkFallbackEventListener(elem, camelName, v, generalLvaluePath)) {
                        triggerWarning(`"${camelName}" is not a valid property`, (_a = elem.ownerShadowRoot) === null || _a === void 0 ? void 0 : _a.getHostNode(), elem);
                    }
                }
            }
            else if (isNativeNode(elem)) {
                if (this.fallbackListenerOnNativeNode) {
                    // compatibilities for legacy event binding syntax
                    const camelName = dashToCamelCase(name);
                    if (!this.checkFallbackEventListener(elem, camelName, v, generalLvaluePath)) {
                        elem.updateAttribute(name, v);
                    }
                }
                else {
                    elem.updateAttribute(name, v);
                }
                if (modelLvaluePath) {
                    elem.setModelBindingListener(name, (value) => {
                        const host = elem.ownerShadowRoot.getHostNode();
                        const nodeDataProxy = Component.getDataProxy(host);
                        nodeDataProxy.replaceDataOnPath(modelLvaluePath, value);
                        nodeDataProxy.applyDataUpdates(false);
                    });
                }
            }
            this.tryCallPropertyChangeListener(elem, name, v);
        };
        // update a attribute
        this.a = (elem, name, v) => {
            elem.updateAttribute(name, v);
            this.tryCallPropertyChangeListener(elem, name, v);
        };
        // set a worklet directive value
        this.wl = (elem, name, value) => {
            if (isComponent(elem)) {
                elem.triggerWorkletChangeLifetime(name, value);
            }
            else {
                // TODO warn unused worklet
            }
        };
        // add a change property binding
        this.p = (elem, name, v, generalLvaluePath) => {
            const tmplArgs = getTmplArgs(elem);
            if (!tmplArgs.changeProp) {
                tmplArgs.changeProp = Object.create(null);
            }
            tmplArgs.changeProp[name] = {
                listener: this.changePropFilter(v, generalLvaluePath, elem, name),
                oldValue: undefined,
            };
        };
        this.shadowRoot = shadowRoot;
        this.procGen = procGen;
        this.fallbackListenerOnNativeNode = fallbackListenerOnNativeNode;
    }
    create(data) {
        const { shadowRoot, procGen } = this;
        const children = procGen(this, true, data);
        this.handleChildrenCreationAndInsert(children.C, shadowRoot, undefined, undefined);
        return children.B;
    }
    update(data, dataUpdatePathTree, bindingMapGen) {
        const { shadowRoot, procGen } = this;
        const children = procGen(this, false, data, dataUpdatePathTree, bindingMapGen);
        this.handleChildrenUpdate(children.C, shadowRoot, undefined, undefined);
        return children.B;
    }
    endBindingMapUpdateForElement(elem) {
        const tmplArgs = elem._$wxTmplArgs;
        if (tmplArgs) {
            if (tmplArgs.staticClassesDirty) {
                tmplArgs.staticClassesDirty = false;
                this.applyClassListUpdates(elem);
            }
            if (tmplArgs.styleNameValuesDirty) {
                tmplArgs.styleNameValuesDirty = false;
                this.applyStyleListUpdates(elem);
            }
        }
        if (isComponent(elem)) {
            if (elem.hasPendingChanges()) {
                const nodeDataProxy = Component.getDataProxy(elem);
                nodeDataProxy.applyDataUpdates(true);
            }
        }
    }
    bindingMapUpdate(field, data, bindingMapGenList) {
        if (this.bindingMapDisabled)
            return false;
        const updaters = bindingMapGenList[field];
        if (!updaters)
            return false;
        let prevElement = null;
        for (let i = 0; i < updaters.length; i += 1) {
            const bindingMapGen = updaters[i];
            bindingMapGen(data, 
            // eslint-disable-next-line no-loop-func
            (elem) => {
                if (prevElement !== null && elem !== prevElement) {
                    this.endBindingMapUpdateForElement(prevElement);
                }
                prevElement = elem;
            }, (elem, v) => {
                elem.textContent = v;
            });
        }
        const elem = prevElement;
        if (elem !== null) {
            this.endBindingMapUpdateForElement(elem);
        }
        return true;
    }
    handleChildrenCreation(children, slotElement, dynamicSlotName) {
        const childNodes = [];
        children(true, 
        // text node
        (textContent, textInit) => {
            if (slotElement && dynamicSlotName !== '') {
                const elem = this.createDynamicPlaceholder(slotElement);
                childNodes.push(elem);
                return;
            }
            const elem = this.shadowRoot.createTextNode(textContent);
            elem.destroyBackendElementOnRemoval();
            if (slotElement)
                Element.setSlotElement(elem, slotElement);
            if (textInit)
                textInit(elem);
            childNodes.push(elem);
        }, 
        // component or native node
        (tagName, genericImpls, propertyInit, children, slot, dynamicSlotValueNames) => {
            if (slotElement && dynamicSlotName !== (slot || '')) {
                const elem = this.createDynamicPlaceholder(slotElement);
                childNodes.push(elem);
                return;
            }
            const elem = this.createCommonElement(tagName, genericImpls, propertyInit, children, dynamicSlotValueNames);
            if (slotElement) {
                Element.setSlotElement(elem, slotElement);
                const tmplArgs = getTmplArgs(elem);
                tmplArgs.dynamicSlotNameMatched = true;
            }
            else if (slot !== undefined) {
                elem.slot = slot;
            }
            childNodes.push(elem);
        }, 
        // wx:if node or template-is node
        (branchKey, branchFunc) => {
            const elem = this.shadowRoot.createVirtualNode('wx:if');
            elem.destroyBackendElementOnRemoval();
            Element.setInheritSlots(elem);
            if (slotElement)
                Element.setSlotElement(elem, slotElement);
            const tmplArgs = getTmplArgs(elem);
            tmplArgs.key = branchKey;
            this.handleChildrenCreationAndInsert(branchFunc, elem, slotElement, dynamicSlotName);
            childNodes.push(elem);
        }, 
        // wx:for node
        (list, key, oriListUpdatePathTree, lvaluePath, itemCallback) => {
            const shadowRoot = this.shadowRoot;
            const elem = shadowRoot.createVirtualNode('wx:for');
            elem.destroyBackendElementOnRemoval();
            Element.setInheritSlots(elem);
            if (slotElement)
                Element.setSlotElement(elem, slotElement);
            const tmplArgs = getTmplArgs(elem);
            tmplArgs.keyList = new RangeListManager(key, list, elem, shadowRoot, (item, index) => {
                const childNode = shadowRoot.createVirtualNode('wx:for-item');
                childNode.destroyBackendElementOnRemoval();
                Element.setInheritSlots(childNode);
                if (slotElement)
                    Element.setSlotElement(elem, slotElement);
                this.handleChildrenCreationAndInsert((isCreation, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode) => {
                    itemCallback(true, item, index, undefined, undefined, lvaluePath ? [...lvaluePath, index] : null, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode);
                }, childNode, slotElement, dynamicSlotName);
                return childNode;
            });
            childNodes.push(elem);
        }, 
        // slot node
        (slotName, slotValueInit, slot) => {
            const elem = this.shadowRoot.createVirtualNode('slot');
            elem.destroyBackendElementOnRemoval();
            Element.setSlotName(elem, dataValueToString(slotName));
            if (slotElement) {
                Element.setSlotElement(elem, slotElement);
            }
            else if (slot !== undefined) {
                elem.slot = slot;
            }
            if (slotValueInit)
                slotValueInit(elem);
            childNodes.push(elem);
        }, 
        // other virtual node
        (children, slot) => {
            if (slot !== undefined) {
                if (slotElement) {
                    if (dynamicSlotName === slot) {
                        const elem = this.shadowRoot.createVirtualNode('virtual');
                        elem.destroyBackendElementOnRemoval();
                        Element.setSlotElement(elem, slotElement);
                        const tmplArgs = getTmplArgs(elem);
                        tmplArgs.dynamicSlotNameMatched = true;
                        this.handleChildrenCreationAndInsert(children, elem, undefined, undefined);
                        childNodes.push(elem);
                    }
                    else {
                        const elem = this.createDynamicPlaceholder(slotElement);
                        childNodes.push(elem);
                    }
                }
                else {
                    const elem = this.shadowRoot.createVirtualNode('virtual');
                    elem.destroyBackendElementOnRemoval();
                    elem.slot = slot;
                    this.handleChildrenCreationAndInsert(children, elem, undefined, undefined);
                    childNodes.push(elem);
                }
            }
            else {
                const elem = this.shadowRoot.createVirtualNode('virtual');
                elem.destroyBackendElementOnRemoval();
                Element.setInheritSlots(elem);
                if (slotElement)
                    Element.setSlotElement(elem, slotElement);
                this.handleChildrenCreationAndInsert(children, elem, slotElement, dynamicSlotName);
                childNodes.push(elem);
            }
        }, undefined, undefined);
        return childNodes;
    }
    handleChildrenCreationAndInsert(children, parentNode, slotElement, dynamicSlotName) {
        const childNodes = this.handleChildrenCreation(children, slotElement, dynamicSlotName);
        if (childNodes.length)
            parentNode.insertChildren(childNodes, -1);
    }
    handleChildrenUpdate(children, parentNode, slotElement, dynamicSlotName) {
        let index = 0;
        const childNodes = slotElement
            ? slotElement.slotNodes.filter((node) => node.parentNode === parentNode)
            : parentNode.childNodes;
        children(false, 
        // text node
        (textContent) => {
            const elem = childNodes[index];
            index += 1;
            if (!elem)
                return;
            if (slotElement) {
                const tmplArgs = getTmplArgs(elem);
                if (!tmplArgs.dynamicSlotNameMatched) {
                    return;
                }
            }
            if (textContent !== undefined) {
                elem.textContent = textContent;
            }
        }, 
        // component or native node
        (tagName, genericImpls, propertyInit, children, slot, dynamicSlotValueNames) => {
            const elem = childNodes[index];
            index += 1;
            if (!elem)
                return;
            const tmplArgs = getTmplArgs(elem);
            if (slotElement) {
                if (dynamicSlotName === (slot || '')) {
                    if (!tmplArgs.dynamicSlotNameMatched) {
                        const newElem = this.createCommonElement(tagName, genericImpls, propertyInit, children, dynamicSlotValueNames);
                        Element.setSlotElement(newElem, slotElement);
                        const newTmplArgs = getTmplArgs(newElem);
                        newTmplArgs.dynamicSlotNameMatched = true;
                        parentNode.replaceChild(newElem, elem);
                        return;
                    }
                }
                else {
                    if (tmplArgs.dynamicSlotNameMatched) {
                        const newElem = this.createDynamicPlaceholder(slotElement);
                        parentNode.replaceChild(newElem, elem);
                    }
                    return;
                }
            }
            propertyInit(elem, false);
            let dynSlot = false;
            if (isComponent(elem)) {
                const sr = this.dynamicSlotUpdate(elem, dynamicSlotValueNames, children);
                if (sr)
                    dynSlot = true;
                if (elem.hasPendingChanges()) {
                    const nodeDataProxy = Component.getDataProxy(elem);
                    nodeDataProxy.applyDataUpdates(true);
                }
                if (sr) {
                    sr.forEachSlot((slot) => {
                        getTmplArgs(slot).dynamicSlotValueUpdatePathTree = Object.create(null);
                    });
                    sr.applySlotUpdates();
                }
            }
            if (!slotElement) {
                if (slot !== undefined)
                    elem.slot = slot;
            }
            if (!dynSlot) {
                this.handleChildrenUpdate(children, elem, undefined, undefined);
            }
            if (tmplArgs.placeholderHandler) {
                tmplArgs.placeholderHandler.onReplace(this.getPlaceholderCallback(elem, tagName, genericImpls, propertyInit, children, dynamicSlotValueNames));
            }
        }, 
        // wx:if node or template-is node
        (branchKey, branchFunc) => {
            const elem = childNodes[index];
            index += 1;
            if (!elem)
                return;
            const prevTmplArgs = getTmplArgs(elem);
            if (prevTmplArgs.key === branchKey) {
                this.handleChildrenUpdate(branchFunc, elem, slotElement, dynamicSlotName);
            }
            else {
                const newElem = this.shadowRoot.createVirtualNode('wx:if');
                newElem.destroyBackendElementOnRemoval();
                Element.setInheritSlots(newElem);
                if (slotElement)
                    Element.setSlotElement(newElem, slotElement);
                const tmplArgs = getTmplArgs(newElem);
                prevTmplArgs.key = tmplArgs.key = branchKey;
                this.handleChildrenCreationAndInsert(branchFunc, newElem, slotElement, dynamicSlotName);
                if (slotElement)
                    parentNode.replaceChild(newElem, elem);
                else
                    parentNode.replaceChildAt(newElem, index - 1);
            }
        }, 
        // wx:for node
        (list, key, oriListUpdatePathTree, lvaluePath, itemCallback) => {
            const elem = childNodes[index];
            index += 1;
            if (!elem)
                return;
            const tmplArgs = getTmplArgs(elem);
            const keyListManager = tmplArgs.keyList;
            keyListManager.diff(list, oriListUpdatePathTree, elem, (item, index) => {
                const childNode = this.shadowRoot.createVirtualNode('wx:for-item');
                childNode.destroyBackendElementOnRemoval();
                Element.setInheritSlots(childNode);
                if (slotElement)
                    Element.setSlotElement(elem, slotElement);
                this.handleChildrenCreationAndInsert((isCreation, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode) => {
                    itemCallback(true, item, index, undefined, undefined, lvaluePath ? [...lvaluePath, index] : null, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode);
                }, childNode, slotElement, dynamicSlotName);
                return childNode;
            }, (item, index, updatePathTree, indexChanged, childNode) => {
                if (!childNode)
                    return;
                this.handleChildrenUpdate((isCreation, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode) => {
                    itemCallback(false, item, index, updatePathTree, indexChanged ? true : undefined, lvaluePath ? [...lvaluePath, index] : null, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode);
                }, childNode, slotElement, dynamicSlotName);
            });
        }, 
        // slot node
        (slotName, slotValueInit, slot) => {
            const elem = childNodes[index];
            index += 1;
            if (!elem)
                return;
            if (slotName !== undefined) {
                Element.setSlotName(elem, dataValueToString(slotName));
            }
            if (!slotElement) {
                if (slot !== undefined)
                    elem.slot = slot;
            }
            if (slotValueInit)
                slotValueInit(elem);
            this.shadowRoot.applySlotValueUpdates(elem);
        }, 
        // other virtual node
        (children, slot) => {
            const elem = childNodes[index];
            index += 1;
            if (!elem)
                return;
            if (slot !== undefined) {
                if (slotElement) {
                    const tmplArgs = getTmplArgs(elem);
                    if (dynamicSlotName === slot) {
                        if (tmplArgs.dynamicSlotNameMatched) {
                            this.handleChildrenUpdate(children, elem, undefined, undefined);
                        }
                        else {
                            const newElem = this.shadowRoot.createVirtualNode('virtual');
                            newElem.destroyBackendElementOnRemoval();
                            Element.setSlotElement(newElem, slotElement);
                            const newTmplArgs = getTmplArgs(newElem);
                            newTmplArgs.dynamicSlotNameMatched = true;
                            this.handleChildrenCreationAndInsert(children, newElem, undefined, undefined);
                            parentNode.replaceChild(newElem, elem);
                        }
                    }
                    else {
                        if (tmplArgs.dynamicSlotNameMatched) {
                            const newElem = this.createDynamicPlaceholder(slotElement);
                            parentNode.replaceChild(newElem, elem);
                        }
                    }
                }
                else {
                    elem.slot = slot;
                    this.handleChildrenUpdate(children, elem, undefined, undefined);
                }
            }
            else {
                this.handleChildrenUpdate(children, elem, slotElement, dynamicSlotName);
            }
        }, undefined, undefined);
    }
    dynamicSlotUpdate(elem, dynamicSlotValueNames, children) {
        const sr = elem.getShadowRoot();
        if ((sr === null || sr === void 0 ? void 0 : sr.getSlotMode()) === 3 /* SlotMode.Dynamic */) {
            sr.setDynamicSlotHandler((slots) => {
                const childNodes = [];
                for (let i = 0; i < slots.length; i += 1) {
                    const { slot, name: slotName, slotValues } = slots[i];
                    const tmplArgs = getTmplArgs(slot);
                    tmplArgs.dynamicSlotValueNames = dynamicSlotValueNames;
                    tmplArgs.dynamicSlotValueUpdatePathTree = undefined;
                    const slotChildNodes = this.handleChildrenCreation((isCreation, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode) => {
                        children(true, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode, slotValues, undefined);
                    }, slot, slotName);
                    childNodes.push(...slotChildNodes);
                }
                if (childNodes.length)
                    elem.insertChildren(childNodes, -1);
            }, (slots) => {
                if (!slots.length)
                    return;
                const sortedSlots = [];
                for (let i = 0; i < slots.length; i += 1) {
                    // ignore empty slots
                    if (slots[i].slotNodes.length)
                        sortedSlots.push(slots[i]);
                }
                sortedSlots.sort((slot1, slot2) => slot1.slotNodes[0].parentIndex - slot2.slotNodes[0].parentIndex);
                let l = 0;
                let r = 0;
                for (let i = 0; i < sortedSlots.length; i += 1) {
                    const slotNodes = sortedSlots[i].slotNodes;
                    const firstIndex = slotNodes[0].parentIndex;
                    let lastIndex = firstIndex;
                    for (let j = slotNodes.length - 1; j > 0; j -= 1) {
                        if (slotNodes[j].parentNode === elem) {
                            lastIndex = slotNodes[j].parentIndex;
                            break;
                        }
                    }
                    if (r !== firstIndex) {
                        if (l >= 0)
                            elem.removeChildren(l, r - l);
                        l = firstIndex;
                    }
                    r = lastIndex + 1;
                }
                if (l !== r)
                    elem.removeChildren(l, r - l);
            }, (slot, slotValues) => {
                const tmplArgs = getTmplArgs(slot);
                const slotValueUpdatePathTrees = tmplArgs.dynamicSlotValueUpdatePathTree;
                if (!slotValueUpdatePathTrees)
                    return;
                tmplArgs.dynamicSlotValueUpdatePathTree = undefined;
                const slotName = slot._$slotName || '';
                this.handleChildrenUpdate((isCreation, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode) => {
                    children(false, defineTextNode, defineElement, defineIfGroup, defineForLoop, defineSlot, definePureVirtualNode, slotValues, slotValueUpdatePathTrees);
                }, elem, slot, slotName);
            }, (slot, name) => {
                var _a;
                const tmplArgs = getTmplArgs(slot);
                if ((_a = tmplArgs.dynamicSlotValueNames) === null || _a === void 0 ? void 0 : _a.includes(name)) {
                    const updatePathTree = (tmplArgs.dynamicSlotValueUpdatePathTree =
                        tmplArgs.dynamicSlotValueUpdatePathTree ||
                            Object.create(null));
                    updatePathTree[name] = true;
                }
            });
            return sr;
        }
        return null;
    }
    createDynamicPlaceholder(slotElement) {
        const elem = this.shadowRoot.createVirtualNode('virtual');
        elem.destroyBackendElementOnRemoval();
        Element.setSlotElement(elem, slotElement);
        const tmplArgs = getTmplArgs(elem);
        tmplArgs.dynamicSlotNameMatched = false;
        return elem;
    }
    checkFallbackEventListener(elem, camelName, value, generalLvaluePath) {
        if (camelName.startsWith('bind')) {
            this.v(elem, camelName.slice('bind'.length), value, false, false, false, true, generalLvaluePath);
        }
        else if (camelName.startsWith('captureBind')) {
            this.v(elem, camelName.slice('captureBind'.length), value, false, false, true, true, generalLvaluePath);
        }
        else if (camelName.startsWith('catch')) {
            this.v(elem, camelName.slice('catch'.length), value, true, false, false, true, generalLvaluePath);
        }
        else if (camelName.startsWith('captureCatch')) {
            this.v(elem, camelName.slice('captureCatch'.length), value, true, false, true, true, generalLvaluePath);
        }
        else if (camelName.startsWith('on')) {
            this.v(elem, camelName.slice('on'.length), value, false, false, false, true, generalLvaluePath);
        }
        else {
            return false;
        }
        return true;
    }
    getPlaceholderCallback(elem, tagName, genericImpls, propertyInit, children, dynamicSlotValueNames) {
        return () => {
            var _a;
            const { using } = this.shadowRoot.resolveComponent(tagName, tagName);
            const replacer = this.shadowRoot.createComponentByDef(tagName, using, genericImpls, (elem) => {
                const sr = isComponent(elem)
                    ? this.dynamicSlotUpdate(elem, dynamicSlotValueNames, children)
                    : null;
                propertyInit(elem, true);
                if (isComponent(elem)) {
                    if (elem.hasPendingChanges()) {
                        const nodeDataProxy = Component.getDataProxy(elem);
                        nodeDataProxy.applyDataUpdates(true);
                    }
                    sr === null || sr === void 0 ? void 0 : sr.applySlotUpdates();
                }
            });
            replacer.destroyBackendElementOnRemoval();
            const replacerShadowRoot = replacer.getShadowRoot();
            const elemShadowRoot = isComponent(elem) ? elem.getShadowRoot() : null;
            const isElemDynamicSlots = (elemShadowRoot === null || elemShadowRoot === void 0 ? void 0 : elemShadowRoot.getSlotMode()) === 3 /* SlotMode.Dynamic */;
            const isReplacerDynamicSlots = (replacerShadowRoot === null || replacerShadowRoot === void 0 ? void 0 : replacerShadowRoot.getSlotMode()) === 3 /* SlotMode.Dynamic */;
            if (isReplacerDynamicSlots !== isElemDynamicSlots) {
                dispatchError(new Error(`The "dynamicSlots" option of component <${replacer.is}> and its placeholder <${elem.is}> should be the same.`), '[render]', isComponent(replacer) ? replacer : replacer.is);
            }
            else if (isReplacerDynamicSlots) {
                (_a = elem.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(replacer, elem);
            }
            else {
                elem.selfReplaceWith(replacer);
            }
        };
    }
    createCommonElement(tagName, genericImpls, propertyInit, children, dynamicSlotValueNames) {
        let dynSlot = false;
        const initPropValues = (elem) => {
            const sr = isComponent(elem)
                ? this.dynamicSlotUpdate(elem, dynamicSlotValueNames, children)
                : null;
            if (sr)
                dynSlot = true;
            propertyInit(elem, true);
            if (isComponent(elem)) {
                if (elem.hasPendingChanges()) {
                    const nodeDataProxy = Component.getDataProxy(elem);
                    nodeDataProxy.applyDataUpdates(true);
                }
                sr === null || sr === void 0 ? void 0 : sr.applySlotUpdates();
            }
        };
        const { placeholderHandler, using } = this.shadowRoot.resolveComponent(tagName, tagName);
        const elem = this.shadowRoot.createComponentByDef(tagName, using, genericImpls, initPropValues);
        if (placeholderHandler) {
            const tmplArgs = getTmplArgs(elem);
            tmplArgs.placeholderHandler = placeholderHandler;
            placeholderHandler.onReplace(this.getPlaceholderCallback(elem, tagName, genericImpls, propertyInit, children, dynamicSlotValueNames));
            new MutationObserver((e) => {
                if (e.type === 'attachStatus' && e.status === 'detached') {
                    placeholderHandler.destroy();
                }
            }).observe(elem, { attachStatus: true });
        }
        elem.destroyBackendElementOnRemoval();
        if (dynSlot) {
            this.bindingMapDisabled = true; // IDEA better binding map disable detection
        }
        else {
            this.handleChildrenCreationAndInsert(children, elem, undefined, undefined);
        }
        return elem;
    }
    tryCallPropertyChangeListener(elem, name, v) {
        var _a;
        const tmplArgs = getTmplArgs(elem);
        if ((_a = tmplArgs.changeProp) === null || _a === void 0 ? void 0 : _a[name]) {
            const lv = tmplArgs.changeProp[name];
            const oldValue = lv.oldValue;
            if (oldValue !== v) {
                lv.oldValue = v;
                const host = elem.ownerShadowRoot.getHostNode();
                safeCallback('Property Change Observer', lv.listener, null, [v, oldValue, host, elem], host);
            }
        }
    }
    applyClassListUpdates(elem) {
        const arr = getTmplArgs(elem).staticClasses;
        elem.setNodeClassList(arr);
        this.tryCallPropertyChangeListener(elem, 'class', arr);
    }
    applyStyleListUpdates(elem) {
        const arr = getTmplArgs(elem).styleNameValues;
        let v = '';
        for (let i = 0; i < arr.length; i += 2) {
            const name = arr[i];
            const value = arr[i + 1];
            v += `${name}:${value};`;
        }
        if (isComponent(elem) && Component.getDataProxy(elem).replaceProperty('style', v)) {
            // empty
        }
        else {
            elem.setNodeStyle(dataValueToString(v), 0 /* StyleSegmentIndex.MAIN */);
        }
        this.tryCallPropertyChangeListener(elem, 'style', v);
    }
    // set change properties filter
    setFnFilter(changePropFilter) {
        this.changePropFilter = changePropFilter;
    }
    // set event listener wrapper
    setEventListenerWrapper(eventListenerWrapper) {
        if (typeof eventListenerWrapper === 'function') {
            this.eventListenerWrapper = eventListenerWrapper;
        }
    }
    // get dev args object
    devArgs(elem) {
        return getTmplDevArgs(elem);
    }
}
export var GeneralLvaluePathPrefix;
(function (GeneralLvaluePathPrefix) {
    GeneralLvaluePathPrefix[GeneralLvaluePathPrefix["Data"] = 0] = "Data";
    GeneralLvaluePathPrefix[GeneralLvaluePathPrefix["Script"] = 1] = "Script";
    GeneralLvaluePathPrefix[GeneralLvaluePathPrefix["InlineScript"] = 2] = "InlineScript";
})(GeneralLvaluePathPrefix || (GeneralLvaluePathPrefix = {}));
//# sourceMappingURL=proc_gen_wrapper.js.map